import { initRuntime, DafAnonymousAuthStrategy } from '@nab/x-runtime';
import { IDigitalData } from '@nab/x-types';
import { checkHashTokenValidity } from './check-token-validity';
import { IOSGIConfig, IWindowADRUM, IWindowShell } from '../interfaces';
import { setShellVersion } from './shell-version';
import { clearTokenOnInit } from './clear-token-on-init';
import { abandonFormNotification } from './abandon-form-notification';
import { getTokenFromHash } from './hash-fragment-token';
import { getTokenCacheStrategy } from '../runtime/auth-token-cache-strategy';
import constants from './constants';

declare global {
	interface Window {
		digitalData: IDigitalData;
		ADRUM: IWindowADRUM;
		nabshell: IWindowShell;
	}
}

let initialised: boolean = false;

/**
 * Initialises the x-runtime
 *
 * - Assumes that the same environment is used by all mini-apps on a page in non-prod
 * - Sets default auth config and defines auth strategies when the first mini-app is loaded
 * - This config is inherited by all subsequent mini-apps but can be overridden if an app requires
 */
export const init = async (config: IOSGIConfig) => {
	if (initialised) {
		return;
	}
	setShellVersion(constants.SHELL_VERSION);
	clearTokenOnInit(config.shellConfig);
	abandonFormNotification(config.shellConfig);

	const token = getTokenFromHash();
	if (token) {
		const dafAnonymousStrategy = config.runtimeConfig.authStrategies?.find(
			strategy => strategy instanceof DafAnonymousAuthStrategy
		);

		if (dafAnonymousStrategy) {
			await checkHashTokenValidity(token, dafAnonymousStrategy);
		} else {
			console.warn('DafAnonymousAuthStrategy not found in auth strategies array.');
		}
	}

	const { shellServices, shellActions } = await initRuntime(config.runtimeConfig);
	
	// Override the token provider set by initRuntime to directly return the shell seed token
	// for interaction/fraud API calls. We don't call authenticate() because it would return
	// a cached mini-app token if one exists. Instead, we directly retrieve the shell seed token.
	if (shellServices.interactionMgr.setTokenProvider) {
		shellServices.interactionMgr.setTokenProvider(() => {
			const seedToken = getTokenCacheStrategy().authTokenCacheStrategy.getSeedToken();
			return Promise.resolve(seedToken?.token);
		});
	}

	initialised = true;
	return {
		shellServices,
		shellActions
	}
};
